import { useEffect } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { useCustomQueryParams, useReportId } from "@ryde/redux";
import { reportFiltersWithValuesAF } from "@ryde/shared";

import { dashboardAppliedFiltersAtom } from "../dynamic-dashboard.atoms";
import { keepFiltersWithKeys } from "../dynamic-dashboard.filters.utils";

/**
 * Drops active bar filters that no card on the dashboard can honour any more —
 * the last card supporting a key having been removed, or a saved view having
 * swapped the layout for one without it.
 *
 * Without this a chip outlives the card that justified it: it stays in the bar,
 * stays in the applied set, and is then silently dropped per card by
 * `pickCardSupportedAppliedFilters`, so the user is looking at a filter that
 * narrows nothing. `pickOfferableFilters` keeps such a key out of the ADD list;
 * this is what keeps that true afterwards, when the cards change underneath a
 * filter the user had already applied.
 *
 * Both stores are pruned. The live one is what the bar renders, and the applied
 * one is what the cards fetch with — leaving the second would keep a removed
 * filter narrowing the dashboard invisibly, which is worse than showing it.
 * Removal goes through the query-string mirror as well, exactly as the reports'
 * own `handleRemoveFilter` does; skipping it would let the filter return from the
 * URL on the next read.
 */
export const usePruneUnhonourableFilters = ({
  keys,
}: {
  keys: string[] | undefined;
}): void => {
  const { reportId } = useReportId();
  const activeFilters = useRecoilValue(reportFiltersWithValuesAF(reportId));
  const setActiveFilters = useSetRecoilState(
    reportFiltersWithValuesAF(reportId),
  );
  const setAppliedFilters = useSetRecoilState(dashboardAppliedFiltersAtom);
  const { setFilterSearchQsByValue } = useCustomQueryParams();

  const keysKey = keys?.join(",");

  useEffect(() => {
    if (!keys) {
      return;
    }

    const nextActive = keepFiltersWithKeys({
      filters: activeFilters ?? {},
      keys,
    });
    if (
      Object.keys(nextActive).length !== Object.keys(activeFilters ?? {}).length
    ) {
      setActiveFilters(nextActive);
      setFilterSearchQsByValue(nextActive);
    }

    // Read through the setter rather than subscribing: the applied set changes on
    // every Apply, and this hook has no reason to re-run for that.
    setAppliedFilters(prev => {
      const next = keepFiltersWithKeys({ filters: prev, keys });

      return Object.keys(next).length === Object.keys(prev).length
        ? prev
        : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keysKey captures keys
  }, [
    keysKey,
    activeFilters,
    setActiveFilters,
    setAppliedFilters,
    setFilterSearchQsByValue,
  ]);
};
